import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useQuery, useMutation } from '@pinia/colada';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import {
  getPayabliStatus,
  updatePayabliFeeAccount,
  PayabliStatus,
} from '@/services/PayabliIntegrationService';

export type { PayabliStatus };

const emptyStatus = (): PayabliStatus => ({
  connected: false,
  defaultPaypointLabel: null,
  paypointCount: 0,
  connectedSince: null,
  feeExpenseAccountId: null,
  sandbox: false,
});

export const usePayabliIntegrationStore = defineStore('payabliIntegrationStore', () => {
  const { handleError } = useSnackbarHandler();

  const loading = ref(false);
  const saving = ref(false);
  const status = ref<PayabliStatus>(emptyStatus());

  // enabled: false — this is only ever driven by fetchStatus() calling .refetch()
  // directly, never auto-fetched off the key changing (same convention as
  // useSettlementStore's polling queries).
  const statusCompanyId = ref<string | null>(null);
  const statusQuery = useQuery({
    key: () => ['payabli-status', statusCompanyId.value],
    query: () => getPayabliStatus(statusCompanyId.value as string),
    enabled: false,
    staleTime: 0,
  });

  async function fetchStatus(companyId: string): Promise<PayabliStatus> {
    loading.value = true;
    statusCompanyId.value = companyId;
    try {
      const state = await statusQuery.refetch(true);
      status.value = state.data as PayabliStatus;
      return status.value;
    } catch (error) {
      status.value = emptyStatus();
      handleError('Failed to load Payabli connection status');
      return status.value;
    } finally {
      loading.value = false;
    }
  }

  const updateFeeAccountMutation = useMutation({
    mutation: (vars: { companyId: string; feeExpenseAccountId: string }) =>
      updatePayabliFeeAccount(vars.companyId, vars.feeExpenseAccountId),
  });

  async function updateFeeAccount(
    companyId: string,
    feeExpenseAccountId: string,
  ): Promise<PayabliStatus> {
    saving.value = true;
    try {
      status.value = await updateFeeAccountMutation.mutateAsync({ companyId, feeExpenseAccountId });
      return status.value;
    } catch (error) {
      handleError('Failed to update the Payabli fee category');
      throw error;
    } finally {
      saving.value = false;
    }
  }

  return {
    loading,
    saving,
    status,
    fetchStatus,
    updateFeeAccount,
  };
});
